import { SelectProjectRole } from "@components/role/select-project-role";
import { MemberRoleReadOnly } from "@components/table/members/members-column/role-column/member-role-read-only";
import { BaseCompanyProjectIdProps } from "@custom-types/base-types";
import { MemberIdentityRoleProps } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { isProjectAdminSelfDowngrade } from "@utils/access-control/project-role-change-guard";
import { formatUserRoleType } from "@utils/data-display";

interface Props extends BaseCompanyProjectIdProps, MemberIdentityRoleProps {
  /** Forces rendering a read-only role label instead of a dropdown */
  isReadOnly?: boolean;

  /** Location from which the member list is rendered, for tracking purposes. */
  location: "role-column";
}

/** Render and adjust member roles in project context. */
export function ProjectMemberRole({
  companyId,
  projectId,
  memberIdentityRole,
  isReadOnly = false,
  location,
}: Props): JSX.Element {
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const currentUser = useAppSelector(currentUserSelector);
  const { canChangeRole } = useHasUserValidPermissionProjectLevel();

  const { changeMemberRole } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "project",
    memberIdentity: memberIdentityRole.identity,
    currentRole: memberIdentityRole.role,
    selectedProject: { id: projectId } as SdbProject,
  });

  const shouldHideDropdown = getShouldHideDropdown();
  /** Determine if the change role dropdown should be hidden */
  function getShouldHideDropdown(): boolean {
    const isResourceOwner = memberIdentityRole.role === "RESOURCE_OWNER";
    const isSelfRoleChangeRestricted = isProjectAdminSelfDowngrade({
      currentUser,
      memberIdentity: memberIdentityRole.identity,
      memberProjectRole: memberIdentityRole.role,
    });

    /**
     * Should hide role change dropdown if:
     * - Project is not editable
     * - User doesn't have a valid role to change
     * - Member is resource owner (Project manager)
     * - Current user is a project admin but not a company admin of this project
     */
    return (
      !isProjectEditable ||
      !canChangeRole ||
      isResourceOwner ||
      isSelfRoleChangeRestricted
    );
  }

  if (isReadOnly) {
    return (
      <MemberRoleReadOnly role={memberIdentityRole.role} variant="tableCell" />
    );
  }

  // Return non-editable member role
  if (shouldHideDropdown) {
    return (
      <span style={{ fontSize: DEFAULT_TEXT_FONT_SIZE }}>
        {formatUserRoleType(memberIdentityRole.role)}
      </span>
    );
  }

  /** Trigger updating member role */
  async function onRoleChange(
    role: FaroCoreApiTypes.ProjectRole
  ): Promise<void> {
    try {
      await changeMemberRole(role);
    } catch {
      // Errors are handled by the hook and slice
    }
  }

  return (
    <SelectProjectRole
      selectedRole={
        memberIdentityRole.role as FaroCoreApiTypes.AssignmentRoleBySubjectType["project"]
      }
      onChange={onRoleChange}
      variant="tableCell"
    />
  );
}
